/**
 * OS Image Type Definitions
 *
 * WICL-206: OS Image catalog types for VM creation
 * Supports Linux, Windows, BSD, and custom images
 */

/**
 * Distro slug derived by the backend catalog (from metadata.osFamily or the image name).
 * These are the exact values the `osType` query filter accepts. The high-level grouping
 * (Linux/Windows/BSD/Custom) lives in `category`, not here.
 */
export type OSType =
  | "ubuntu"
  | "debian"
  | "centos"
  | "fedora"
  | "rocky"
  | "alma"
  | "windows"
  | "windows-server"
  | "freebsd"
  | "openbsd";

export interface OSImageSpecs {
  cores: number;
  /** Memory in GB (converted from the backend's MB). */
  memory: number;
  disk: number;
}

export interface OSImage {
  id: string;
  name: string;
  version?: string; // Optional - backend doesn't provide
  osType?: OSType; // Optional - backend doesn't provide
  category: string;
  description?: string; // Optional - can be null
  iconUrl?: string; // Optional - can be null
  minimumSpecs?: OSImageSpecs; // Optional - backend doesn't provide
  recommendedSpecs?: OSImageSpecs; // Optional - backend doesn't provide
  isActive?: boolean; // Optional - backend doesn't provide in list
  isFeatured: boolean;
  sortOrder?: number; // Optional - backend provides but we might not need
  supportedFeatures?: string[]; // Optional - backend doesn't provide
  tags?: string[]; // Optional - can be null
  // No longer sent by the catalog API: the "used by N VMs" line was removed from the detail
  // dialog. Restore by un-commenting UsageCount in OSImageListDto / GetOSImagesQueryHandler.
  usageCount?: number;
  estimatedMonthlyCost?: number; // Smallest currency unit (renamed from estimatedMonthlyCostCents)
  estimatedHourlyCost?: number; // Smallest currency unit (also added to match backend)
  currency?: string; // Backend provides this
  /**
   * False when an operator marked this Windows image as unable to activate a licence
   * (admin console → OS image → Windows License). Absent or true means no restriction.
   */
  licenseActivationSupported?: boolean;
  /** Operator-written notice shown when licence activation is not supported. */
  licenseWarningMessage?: string;
}

/** True when the image is Windows and an operator marked it as unable to activate a licence. */
export function hasUnsupportedWindowsLicense(
  image: OSImage | null | undefined,
): boolean {
  return isWindowsOSImage(image) && image?.licenseActivationSupported === false;
}

/**
 * The catalog normally provides osType, but category/name fallbacks keep the
 * create flow safe for older catalog records that do not have that metadata.
 */
export function isWindowsOSImage(image: OSImage | null | undefined): boolean {
  if (!image) return false;

  return [image.osType, image.category, image.name].some((value) =>
    value?.toLocaleLowerCase().includes("windows"),
  );
}

export interface OSImageFilters {
  category?: string;
  osType?: OSType;
  search?: string;
  featured?: boolean;
}

export interface OSImageListResponse {
  items: OSImage[];
  total: number;
}

export interface ProductLineSpecConfig {
  key: string;
  labels: Record<string, string>;
  descriptions?: Record<string, string>;
  cores: number;
  memoryGb: number;
  diskGb: number;
  additionalIps: number;
  backupCount: number;
  snapshotCount: number;
  isDefault: boolean;
  sortOrder: number;
}

export interface ProductLineSpecRange {
  min: number;
  max: number;
  step: number;
}

export interface ProductLineSpecLimits {
  cores?: ProductLineSpecRange | null;
  memoryGb?: ProductLineSpecRange | null;
  diskGb?: ProductLineSpecRange | null;
  ips?: ProductLineSpecRange | null;
  backupCount?: ProductLineSpecRange | null;
  snapshotCount?: ProductLineSpecRange | null;
}

export interface ProductLineDto {
  id: string;
  name: string;
  slug: string;
  tier: string;
  pricePerCpuCore: number;
  pricePerRamGb: number;
  pricePerSsdGb: number;
  currency: string;
  priceFirstIp: number;
  priceAdditionalIp: number;
  pricePerBackupGb: number;
  pricePerSnapshotGb: number;
  defaultBackupCount: number;
  defaultSnapshotCount: number;
  /** CPU chip types available for this product line (e.g. ["Intel", "AMD"]). Empty when unconfigured. */
  availableChipTypes: string[];
  /** Admin-defined spec presets. Empty when unconfigured — the wizard falls back to its defaults. */
  specConfigs?: ProductLineSpecConfig[];
  /** Per-tier slider bounds. Null/absent = platform defaults. */
  specLimits?: ProductLineSpecLimits | null;
}
